/**
 * Tiroir « Filtres & tri » — l'ouverture, la fermeture, et rien d'autre.
 *
 * Mécanique portée de preview/kb-react/src/pages/CollectionPage.jsx, moins
 * tout ce qui filtrait les cartes côté client : ici c'est Shopify qui rend
 * la liste filtrée, le formulaire du snippet part en GET et la page revient
 * juste. Ce fichier n'a donc aucune logique de filtrage.
 *
 * Le panneau n'est pas un <dialog> — contrairement au tiroir de menu, qui
 * s'en sort avec showModal(). Un <dialog> ne peut pas rester dans le flux
 * quand JavaScript manque, or le formulaire de filtres doit rester
 * utilisable sans JS (voir le <noscript> de snippets/collection-filters.liquid).
 * Le prix à payer : Échap, le verrou de défilement et le piège à focus sont
 * à écrire à la main, ce que fait ce fichier.
 *
 * Délégation sur le document plutôt qu'un écouteur par bouton : l'éditeur de
 * thème remplace le HTML de la section à chaque modification de réglage, ce
 * qui décrocherait des écouteurs posés sur les éléments. C'est aussi ce qui
 * permet au déclencheur [data-cfopen] de vivre dans la barre de la section,
 * loin du panneau dans le DOM.
 */

/* Ce que le tiroir considère comme focalisable. `:not([disabled])` écarte
   les valeurs de filtre grisées : les tabuler ne mènerait nulle part. */
const FOCALISABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let declencheur = null;
let minuterie = 0;

const panneau = () => document.querySelector('[data-cf]');
const voile = () => document.querySelector('[data-cfveil]');
const estOuvert = () => {
  const pan = panneau();
  return Boolean(pan) && !pan.hidden;
};

/**
 * La durée de sortie n'est pas écrite ici : elle est lue dans le style
 * calculé du panneau. section-collection-filters.css reste la seule source
 * de vérité du geste, et `prefers-reduced-motion` — qui y ramène la
 * transition à 0,01s — raccourcit l'attente sans code supplémentaire.
 */
function dureeSortie(element) {
  const declaree = getComputedStyle(element).transitionDuration;
  const secondes = parseFloat(declaree);
  return Number.isNaN(secondes) ? 340 : secondes * 1000;
}

function ouvrirTiroir(bouton) {
  const pan = panneau();
  const vei = voile();
  if (!pan || !vei) return;

  clearTimeout(minuterie);
  declencheur = bouton;

  pan.hidden = false;
  vei.hidden = false;

  /* Le passage de `display: none` à `display: flex` et l'ajout de .on dans
     la même image seraient fusionnés par le navigateur : la transition ne
     jouerait pas. La lecture de offsetWidth force le calcul intermédiaire. */
  requestAnimationFrame(() => {
    void pan.offsetWidth;
    pan.classList.add('on');
    vei.classList.add('on');
  });

  /* Le verrou porte sur <html>, comme la règle `html:has(dialog[scroll-lock])`
     de critical.css : deux verrous sur deux éléments différents finiraient
     par se contredire. */
  document.documentElement.style.overflow = 'hidden';

  /* Le déclencheur peut désigner le groupe à dérouler — la barre de la
     section peut ainsi proposer un raccourci « Trier » qui ouvre le tiroir
     directement sur le tri. */
  const cible = bouton && bouton.getAttribute('data-cfopen');
  if (cible) {
    pan.querySelectorAll('[data-cfsec]').forEach((groupe) => {
      groupe.open = groupe.getAttribute('data-cfsec') === cible;
    });
  }

  pan.querySelector('[data-cfclose]')?.focus();
}

function fermerTiroir() {
  const pan = panneau();
  const vei = voile();
  if (!pan || !vei || pan.hidden) return;

  pan.classList.remove('on');
  vei.classList.remove('on');
  document.documentElement.style.overflow = '';

  /* `hidden` revient seulement une fois le panneau sorti de l'écran, sinon
     il disparaîtrait d'un coup au lieu de glisser. */
  clearTimeout(minuterie);
  minuterie = setTimeout(() => {
    pan.hidden = true;
    vei.hidden = true;
  }, dureeSortie(pan));

  /* Rendre le focus au déclencheur : sans ça, le clavier repart du début de
     la page à chaque fermeture. */
  declencheur?.focus();
  declencheur = null;
}

/**
 * Piège à focus. Le panneau se dit `aria-modal="true"` : il doit tenir cette
 * promesse, sinon la tabulation part naviguer dans la page derrière le voile,
 * que le lecteur d'écran a déjà déclarée hors-jeu.
 */
function piegerFocus(evenement) {
  const pan = panneau();
  if (!pan) return;

  const cibles = [...pan.querySelectorAll(FOCALISABLES)].filter(
    (element) => element.offsetParent !== null || element === document.activeElement
  );
  if (cibles.length === 0) return;

  const premier = cibles[0];
  const dernier = cibles[cibles.length - 1];
  const actif = document.activeElement;

  if (evenement.shiftKey && (actif === premier || !pan.contains(actif))) {
    evenement.preventDefault();
    dernier.focus();
    return;
  }
  if (!evenement.shiftKey && (actif === dernier || !pan.contains(actif))) {
    evenement.preventDefault();
    premier.focus();
  }
}

/* Un seul écouteur clavier, posé une fois pour toutes et gardé par l'état
   plutôt qu'ajouté puis retiré à chaque ouverture : une section rechargée
   par l'éditeur ne peut pas laisser d'écouteur orphelin derrière elle. */
document.addEventListener('keydown', (evenement) => {
  if (!estOuvert()) return;

  if (evenement.key === 'Escape') {
    evenement.preventDefault();
    fermerTiroir();
    return;
  }
  if (evenement.key === 'Tab') piegerFocus(evenement);
});

document.addEventListener('click', (evenement) => {
  const ouvreur = evenement.target.closest('[data-cfopen]');
  if (ouvreur) {
    ouvrirTiroir(ouvreur);
    return;
  }

  if (evenement.target.closest('[data-cfclose]') || evenement.target.closest('[data-cfveil]')) {
    fermerTiroir();
  }
});

/**
 * L'éditeur de thème remplace le HTML de la section : si le tiroir était
 * ouvert, son panneau vient de disparaître avec le verrou de défilement
 * encore posé. On remet la page dans un état propre.
 */
document.addEventListener('shopify:section:load', () => {
  clearTimeout(minuterie);
  declencheur = null;
  document.documentElement.style.overflow = '';

  const pan = panneau();
  const vei = voile();
  pan?.classList.remove('on');
  vei?.classList.remove('on');
  if (pan) pan.hidden = true;
  if (vei) vei.hidden = true;
});
